/**
 * CancelDrawnOutModal
 *
 * Surfaced when the admin clicks "Cancel Drawn-Out Entries" on the
 * rodeo draw screen. Distinct from publish — publish/finalize no
 * longer auto-cancels anyone. This action is the explicit cleanup
 * step the admin runs AFTER the org's 24-hour call-in window closes
 * (many orgs give drawn-out contestants that window to claim an
 * open slot before refunds run).
 *
 * Listing affected contestants by name with refund estimates is
 * load-bearing — the admin is about to cancel paid registrations,
 * so the destructive action must be auditable in the UI before they
 * confirm. The backend re-derives the unplaced + still-active set at
 * cancel time, so any drift between this modal and the actual cancel
 * list is resolved server-side. The modal's source-of-truth list
 * comes from `useDrawPublishPreview` (shared endpoint that the
 * backend has loosened to work against any execution state, not just
 * draft) so what the admin reads here matches what will be cancelled.
 *
 * Team roping: a team is ONE draw unit but TWO individual
 * registrations (header + heeler), each with its own member status
 * and payment. Cancelling a drawn-out team cancels and refunds BOTH.
 * The summary therefore counts teams as a single ENTRY but breaks the
 * member/paid/refund math down per CONTESTANT, and the table lists
 * both teammates so the admin sees exactly who is refunded what.
 */

import { useEffect, useState } from 'react';
import { AlertTriangle, Loader2, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type {
  PublishPreview,
  PublishPreviewEntry,
  PublishPreviewTeamPartner,
} from '@/api/extensions/useDrawOps';

interface CancelDrawnOutModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Fires with the list of entries (by primary `registration_id`) the admin
   * toggled to ALSO refund the transaction fee. Entries not in the list keep
   * the fee retained (the default).
   */
  onConfirm: (refundFeeRegistrationIds: string[]) => void;
  preview: PublishPreview | undefined;
  isLoadingPreview: boolean;
  isCancelling: boolean;
}

/**
 * Whether an entry has a transaction fee that could be refunded — only paid
 * entries with a non-zero retained-fee estimate (on the header or heeler) get
 * a toggle. Unpaid / zero-fee entries have nothing to give back.
 */
function entryHasRefundableFee(u: PublishPreviewEntry): boolean {
  const own = u.has_payment && u.retained_fee_estimate > 0;
  const partner =
    !!u.team_partner &&
    u.team_partner.has_payment &&
    u.team_partner.retained_fee_estimate > 0;
  return own || partner;
}

const fmtUSD = (n: number) =>
  n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/**
 * The subset of fields shared by a preview entry and its team partner.
 * Both contribute to the member/paid/refund tallies identically — the
 * only difference is that an entry is the "draw unit" the count keys
 * off, while a partner rides along on that unit.
 */
interface Contestant {
  non_member: boolean;
  has_payment: boolean;
  payment_amount: number;
  refund_amount_estimate: number;
  retained_fee_estimate: number;
}

export function CancelDrawnOutModal({
  open,
  onClose,
  onConfirm,
  preview,
  isLoadingPreview,
  isCancelling,
}: CancelDrawnOutModalProps) {
  const unplaced = preview?.unplaced ?? [];

  // Per-entry "refund the transaction fee too" toggles, keyed by primary
  // registration_id. DEFAULT IS OFF for every entry — the standard policy is
  // to RETAIN the (non-refundable) transaction fee, so an admin must opt in
  // per entry to give it back. Reset to empty each time the modal opens so a
  // prior session's choices never carry over.
  const [refundFeeIds, setRefundFeeIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (open) setRefundFeeIds(new Set());
  }, [open]);

  const toggleRefundFee = (registrationId: string, checked: boolean) => {
    setRefundFeeIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(registrationId);
      else next.delete(registrationId);
      return next;
    });
  };

  // Entries = draw units (a team counts as one). Contestants = people
  // (a team contributes two). The header/count keys off entries; the
  // money + member math keys off contestants.
  const entryCount = unplaced.length;
  const contestants: Contestant[] = [];
  for (const u of unplaced) {
    contestants.push(u);
    if (u.team_partner) contestants.push(u.team_partner);
  }
  const hasTeams = unplaced.some((u) => !!u.team_partner);
  // Partnerless TR rows are flagged by the backend with a stable
  // `unplaced_reason` string. We surface them in the modal copy so the
  // admin knows the SAME button now handles two distinct cohorts.
  // Drawn-out rows have varied reasons (or null) — the partnerless reason
  // is a unique marker we set in publish.service.ts.
  const PARTNERLESS_REASON = 'No partner — team-roping entry was never paired';
  const partnerlessCount = unplaced.filter(
    (u) => u.unplaced_reason === PARTNERLESS_REASON,
  ).length;
  const drawnOutCount = entryCount - partnerlessCount;

  // Split the contestant set so the admin sees at a glance how many
  // people result in a refund vs. a status-only flip (registered but
  // never paid). Totals only sum the paid bucket — and reflect the
  // per-entry fee toggles live: refunding the fee returns the full
  // payment and retains $0 for both halves of that draw unit.
  const memberCount = contestants.filter((c) => !c.non_member).length;
  const nonMemberCount = contestants.filter((c) => c.non_member).length;
  let paidCount = 0;
  let unpaidCount = 0;
  let totalPaid = 0;
  let totalRefund = 0;
  let totalRetained = 0;
  for (const u of unplaced) {
    const refundFee = refundFeeIds.has(u.registration_id);
    const people: Contestant[] = u.team_partner ? [u, u.team_partner] : [u];
    for (const c of people) {
      if (!c.has_payment) {
        unpaidCount++;
        continue;
      }
      paidCount++;
      totalPaid += c.payment_amount;
      totalRefund += refundFee ? c.payment_amount : c.refund_amount_estimate;
      totalRetained += refundFee ? 0 : c.retained_fee_estimate;
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => (!v ? onClose() : null)}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
            <AlertTriangle className="h-5 w-5" />
            {/* Title adapts to whichever cohort(s) are present. ONE button
                covers both per the research-card recommendation; the title
                names what's actually about to happen so the admin isn't
                surprised when partnerless rows show up alongside drawn-out. */}
            Cancel {entryCount} {entryCount === 1 ? 'entry' : 'entries'}?
            {drawnOutCount > 0 && partnerlessCount > 0 && (
              <span className="text-xs font-normal text-amber-600 dark:text-amber-400">
                ({drawnOutCount} drawn-out · {partnerlessCount} partnerless)
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="pt-2 leading-relaxed">
            The following {entryCount} contestant
            {entryCount === 1 ? ' entry is' : ' entries are'} not placed
            in the draw —{' '}
            {drawnOutCount > 0 && partnerlessCount > 0 ? (
              <>some were drawn out, others never got a partner.</>
            ) : drawnOutCount > 0 ? (
              <>not drawn or set to draw out.</>
            ) : (
              <>team-roping entries that were never paired with a partner.</>
            )}{' '}
            Cancelling now will <strong>cancel their entries</strong> and{' '}
            <strong>issue refunds</strong> for any payments — the transaction
            fee is <strong>retained by default</strong>. Flip{' '}
            <em>Refund fee?</em> on any entry to also return its transaction
            fee. Unpaid entries will be cancelled with no refund (nothing was
            charged).{' '}
            {hasTeams && (
              <>
                Team roping entries cancel{' '}
                <strong>both teammates</strong> together.{' '}
              </>
            )}
            <strong>This action cannot be reversed.</strong>
          </DialogDescription>
        </DialogHeader>

        {isLoadingPreview ? (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading preview…
          </div>
        ) : entryCount === 0 ? (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100">
            No drawn-out or partnerless contestants remain — nothing to cancel.
          </div>
        ) : (
          <>
            {/* Summary card at the top: at-a-glance breakdown so the
                admin doesn't have to mentally aggregate the table
                below. The "Total" card shows entries (teams count once)
                with the contestant headcount underneath; member / paid /
                refund are tallied per contestant so a team's two
                registrations are never collapsed into one line. */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="rounded-md border border-amber-200 dark:border-amber-900/60 bg-amber-50 dark:bg-amber-950/30 px-3 py-2">
                <div className="text-[11px] uppercase tracking-wide text-amber-800/80 dark:text-amber-200/80">
                  Entries
                </div>
                <div className="text-lg font-semibold text-amber-900 dark:text-amber-100 tabular-nums">
                  {entryCount}
                </div>
                <div className="text-[11px] text-amber-800/70 dark:text-amber-200/70">
                  {contestants.length === entryCount
                    ? 'to cancel'
                    : `${contestants.length} contestants`}
                </div>
              </div>
              <div className="rounded-md border bg-card px-3 py-2">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Members / non
                </div>
                <div className="text-lg font-semibold tabular-nums">
                  {memberCount} / {nonMemberCount}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  member · non-member
                </div>
              </div>
              <div className="rounded-md border bg-card px-3 py-2">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Paid / unpaid
                </div>
                <div className="text-lg font-semibold tabular-nums">
                  {paidCount} / {unpaidCount}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {paidCount === 0
                    ? 'no refunds owed'
                    : `${fmtUSD(totalPaid)} collected`}
                </div>
              </div>
              <div className="rounded-md border border-emerald-200 dark:border-emerald-900/60 bg-emerald-50 dark:bg-emerald-950/30 px-3 py-2">
                <div className="text-[11px] uppercase tracking-wide text-emerald-900/80 dark:text-emerald-200/80">
                  Refund total
                </div>
                <div className="text-lg font-semibold text-emerald-900 dark:text-emerald-100 tabular-nums">
                  {fmtUSD(totalRefund)}
                </div>
                <div className="text-[11px] text-emerald-900/70 dark:text-emerald-200/70">
                  fees kept {fmtUSD(totalRetained)}
                </div>
              </div>
            </div>

            <div className="max-h-72 overflow-y-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted/60 text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">Contestant</th>
                    <th className="px-3 py-2 font-medium">Reason</th>
                    <th className="px-3 py-2 text-right font-medium">Paid</th>
                    <th className="px-3 py-2 text-right font-medium">
                      Refund
                    </th>
                    <th className="px-3 py-2 text-center font-medium">
                      Refund fee?
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {unplaced.map((row) => (
                    <PreviewRows
                      key={row.registration_id}
                      row={row}
                      refundFee={refundFeeIds.has(row.registration_id)}
                      feeEligible={entryHasRefundableFee(row)}
                      onToggleRefundFee={(checked) =>
                        toggleRefundFee(row.registration_id, checked)
                      }
                      disabled={isCancelling}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-xs text-muted-foreground leading-relaxed">
              Refunds are processed via Stripe. Exact amounts may differ
              by a few cents under bulk-payment or non-member surcharge
              math; the totals above are for guidance.
            </p>
          </>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isCancelling}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => onConfirm(Array.from(refundFeeIds))}
            disabled={
              isCancelling || isLoadingPreview || entryCount === 0
            }
          >
            {isCancelling ? (
              <>
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                Cancelling…
              </>
            ) : (
              <>
                Cancel {entryCount} {entryCount === 1 ? 'entry' : 'entries'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Per-person money cells — shared by the primary and partner rows. When the
 * entry's `refundFee` toggle is on, the full payment is returned and the fee
 * sub-text flips from "fee retained" to "fee refunded" so the admin sees the
 * live effect of the toggle.
 */
function MoneyCells({
  hasPayment,
  paymentAmount,
  refundAmount,
  retainedFee,
  refundFee,
}: {
  hasPayment: boolean;
  paymentAmount: number;
  refundAmount: number;
  retainedFee: number;
  refundFee: boolean;
}) {
  const effectiveRefund = refundFee ? paymentAmount : refundAmount;
  return (
    <>
      <td className="px-3 py-2 text-right tabular-nums">
        {hasPayment ? fmtUSD(paymentAmount) : '—'}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        {hasPayment ? (
          <>
            <div className="font-medium">{fmtUSD(effectiveRefund)}</div>
            <div className="text-[10px] text-muted-foreground">
              {refundFee
                ? retainedFee > 0
                  ? `fee refunded ${fmtUSD(retainedFee)}`
                  : 'full refund'
                : `fee retained ${fmtUSD(retainedFee)}`}
            </div>
          </>
        ) : (
          <span className="text-muted-foreground">no payment</span>
        )}
      </td>
    </>
  );
}

function MemberBadge({ nonMember }: { nonMember: boolean }) {
  return nonMember ? (
    <Badge
      variant="outline"
      className="text-[10px] border-orange-400/60 bg-orange-50 text-orange-900 dark:border-orange-700/60 dark:bg-orange-950/40 dark:text-orange-100"
    >
      Non-member
    </Badge>
  ) : (
    <Badge
      variant="outline"
      className="text-[10px] border-sky-400/60 bg-sky-50 text-sky-900 dark:border-sky-700/60 dark:bg-sky-950/40 dark:text-sky-100"
    >
      Member
    </Badge>
  );
}

/**
 * Renders one preview entry. For a solo entry that's a single row; for a
 * team roping team it's the header row plus an indented heeler row, so
 * the team reads as one grouped entry while each contestant keeps their
 * own member badge and refund figures.
 */
function PreviewRows({
  row,
  refundFee,
  feeEligible,
  onToggleRefundFee,
  disabled,
}: {
  row: PublishPreviewEntry;
  refundFee: boolean;
  feeEligible: boolean;
  onToggleRefundFee: (checked: boolean) => void;
  disabled: boolean;
}) {
  const partner: PublishPreviewTeamPartner | null = row.team_partner;

  return (
    <>
      <tr className="border-t last:border-b">
        <td className="px-3 py-2">
          <div className="font-medium">{row.display_name}</div>
          <div className="flex flex-wrap gap-1 pt-1">
            <MemberBadge nonMember={row.non_member} />
            {row.is_draw_out_pref && (
              <Badge
                variant="secondary"
                className="text-[10px] bg-red-100 text-red-900 dark:bg-red-950 dark:text-red-200"
              >
                Draw-out
              </Badge>
            )}
            {partner && (
              <Badge
                variant="outline"
                className="text-[10px] gap-1 border-violet-400/60 bg-violet-50 text-violet-900 dark:border-violet-700/60 dark:bg-violet-950/40 dark:text-violet-100"
              >
                <Users className="h-2.5 w-2.5" />
                Team
              </Badge>
            )}
          </div>
        </td>
        <td className="px-3 py-2 text-muted-foreground">
          {row.unplaced_reason ?? '—'}
        </td>
        <MoneyCells
          hasPayment={row.has_payment}
          paymentAmount={row.payment_amount}
          refundAmount={row.refund_amount_estimate}
          retainedFee={row.retained_fee_estimate}
          refundFee={refundFee}
        />
        {/* Per-entry fee toggle. Governs the whole draw unit — for a team it
            refunds both the header's and heeler's fee together, so it only
            renders on the primary row. Hidden when there's no fee to give
            back (unpaid / zero-fee entries). */}
        <td className="px-3 py-2 text-center align-middle">
          {feeEligible ? (
            <Switch
              checked={refundFee}
              onCheckedChange={onToggleRefundFee}
              disabled={disabled}
              aria-label={`Refund transaction fee for ${row.display_name}${
                partner ? ' and team partner' : ''
              }`}
            />
          ) : (
            <span className="text-[10px] text-muted-foreground">—</span>
          )}
        </td>
      </tr>

      {partner && (
        <tr className="bg-muted/30">
          <td className="px-3 py-2 pl-6 border-l-2 border-l-violet-300 dark:border-l-violet-800">
            <div className="font-medium">{partner.display_name}</div>
            <div className="flex flex-wrap gap-1 pt-1">
              <MemberBadge nonMember={partner.non_member} />
              {partner.position && (
                <Badge variant="outline" className="text-[10px]">
                  {partner.position}
                </Badge>
              )}
            </div>
          </td>
          <td className="px-3 py-2 text-muted-foreground">
            Team partner of {row.display_name}
          </td>
          <MoneyCells
            hasPayment={partner.has_payment}
            paymentAmount={partner.payment_amount}
            refundAmount={partner.refund_amount_estimate}
            retainedFee={partner.retained_fee_estimate}
            refundFee={refundFee}
          />
          <td className="px-3 py-2 text-center align-middle">
            <span className="text-[10px] text-muted-foreground">
              with team
            </span>
          </td>
        </tr>
      )}
    </>
  );
}
